import { useQuery } from '@tanstack/react-query';
import { API } from 'network/api';
import { useMemo } from 'react';

interface SeasonIndex {
  id: string;
  seasonCode: string;
  seasonType: string;
  startDate: string | Date;
  endDate: string | Date;
  country: string;
  targetParticipatingArea: number;
  isCurrentSeason: boolean;
  seasonNumber: number;
}

const useSeasonIndex = (country: string) => {
  const options = useMemo(
    () => ({
      params: {
        'country.equals': country,
      },
    }),
    [country],
  );

  const { data, isLoading, refetch, error } = useQuery<SeasonIndex[]>({
    queryKey: ['season-index', country, options],
    queryFn: async () => {
      const res = await API.get('/season/country/seasonIndex', options);
      return res?.data;
    },
    refetchOnMount: false,
    enabled: !!country,
    staleTime: 1000 * 60 * 5, // 5 minutes - wouldn't change very often
  });

  const seasonIndexFilterOptions = useMemo(() => {
    if (!data) return [];
    return data?.map((season: SeasonIndex) => ({
      label: season?.seasonCode,
      value: season?.id,
    }));
  }, [data]);

  const currentSeason = useMemo(() => {
    if (!data) return undefined;
    return data?.filter((val) => val.isCurrentSeason)?.[0];
  }, [data]);

  return useMemo(
    () => ({
      data,
      refetch,
      isLoading,
      error,
      seasonIndexFilterOptions,
      currentSeason,
    }),
    [data, refetch, isLoading, error, seasonIndexFilterOptions],
  );
};

export default useSeasonIndex;
